import React, { useEffect } from 'react';
import { Accessibility } from '../../../utils/Accessibility/Accessibility';
import { FontAwesomeConfigurations } from '../../../utils/Configuration/FontAwesomeConfiguration';

interface JUNOAddToCalendarButtonProps {
  content: any;
  helperText?: string;
}

const JUNOAddToCalendarButton: React.FC<JUNOAddToCalendarButtonProps> = ({ ...props }) => {
  const [optionsOpen, setOptionsOpen] = React.useState(false);
  const options = CrowdHub_App.CONFIG.CALENDAR_EVENT_SHARE_OPTIONS;
  const optionsRef = React.useRef(null);

  useOptionsPopoverListener(optionsRef, optionsOpen, setOptionsOpen);

  const handleCalendarButtonClick = () => {
    if (App.INSTANCE.CurrentUser.IsLoggedIn() == false) {
      if (CrowdHub_App.CONFIG.ALLOW_CALENDAR_ONLOGGEDOUT == false) {
        promptLogin();
      } else {
        openOptions(setOptionsOpen);
      }
    } else {
      if (props.content.hasAccess() == false) {
        App.INSTANCE.checkAccessLevel(props.content);
      } else {
        openOptions(setOptionsOpen);
      }
    }
  };

  const calendarButtons = options.map((option, i) => {
    const svg = Resources.svg[option.icon].src;
    const containerHTML = (
      <div
        className="event_option_container"
        role={'button'}
        tabIndex={0}
        onClick={() => {
          optionSelected(option.actionType);
        }}
        key={i}
      >
        <img className="icon" src={`data:image/svg+xml;base64,${btoa(svg)}`}></img>
        <div className="text">{option.name}</div>
        {option.actionType !== 'ICS' && <div className="text online-indicator">(online)</div>}
      </div>
    );
    return containerHTML;
  });

  const optionSelected = actionType => {
    setOptionsOpen(false);

    if (actionType === 'ICS') {
      doICS(props.content);
    }

    if (actionType === 'GOOGLE_LINK') {
      doGoogle(props.content);
    }

    if (actionType === 'OFFICE_365_LINK') {
      doMS(props.content, 'office');
    }

    if (actionType === 'OUTLOOK_COM_LINK') {
      doMS(props.content, 'live');
    }

    if (actionType === 'YAHOO_LINK') {
      doYahoo(props.content);
    }
  };

  const doICS = content => {
    const params = {
      content_ids: [content.Id],
      location: encodeURIComponent(getLocation(content)),
      CALENDAR_INVITE_ALARM: CrowdHub_App.CONFIG.CALENDAR_INVITE_ALARM,
      CALENDAR_INVITE_ALARM_MESSAGE: CrowdHub_App.CONFIG.CALENDAR_INVITE_ALARM_MESSAGE,
      CALENDAR_INVITE_TIME_BEFORE: CrowdHub_App.CONFIG.CALENDAR_INVITE_TIME_BEFORE,
    };
    const qs = GlobalHelperFunctions.createUrlQueryString(params);
    const url = `${CrowdHub_App.CONFIG.BASE_URL}custom_api/api_ical.php?${qs}`;
    App.INSTANCE.openExternalUrl(url);
    joinEvent(content);
  };

  const doGoogle = content => {
    const dates = formatDate(content.MomentDateStart) + '/' + formatDate(content.MomentDateEnd);
    let params = {
      action: 'TEMPLATE',
      text: content.Name,
      details: `${getLocation(content)} - ${content.Description || content.SocialDescription}`,
      dates: dates,
    };

    let uri = `https://calendar.google.com/calendar/r/eventedit?${GlobalHelperFunctions.createUrlQueryString(params)}`;

    App.INSTANCE.openExternalUrl(uri);
  };

  const doMS = (content, type) => {
    if (type !== 'live' && type !== 'office') {
      throw Error('doMS requires parameter "live" for Outlook.com or "office" for Office 365');
    }

    let params = {
      rru: 'addevent',
      subject: content.Name,
      body: `${getLocation(content)} - ${content.Description || content.SocialDescription}`,
      // location: this.getLocation(),
    };

    let timezone = encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone);

    let unEncodedParams = {
      path: '/calendar/action/compose',
      startdt: formatMSDate(content.MomentDateStart),
      enddt: formatMSDate(content.MomentDateEnd),
      tz: timezone,
    };

    let uri = `https://outlook.${type}.com/calendar/0/deeplink/compose?${GlobalHelperFunctions.createUrlQueryString(
      params,
      unEncodedParams,
    )}`;

    if (App.CORDOVA == false) {
      window.open(uri, '_blank');
    } else {
      App.INSTANCE.openExternalUrl(uri);
    }
  };

  const doYahoo = content => {
    const start = formatDate(content.MomentDateStart);
    var end = formatDate(content.MomentDateEnd);
    // var duration = moment.duration(this.mContent.MomentDateStart.diff(end)).asMinutes();

    var params = {
      v: 60,
      title: content.Name,
      st: start,
      et: end,
      desc: `${getLocation(content)} - ${content.Description || content.SocialDescription}`,
      view: 'd',
      type: 20,
      // in_loc: this.getLocation(),
    };
    var uri = `https://calendar.yahoo.com/?${GlobalHelperFunctions.createUrlQueryString(params)}`;
    /* var uri = [
              'https://calendar.yahoo.com',
              '?v=60',
              '&view=d',
              '&type=20',
              '&title=' + encodeURIComponent(self.mContent.Name),
              '&st=' + start,
              '&dur=' + duration,
              '&desc=' + encodeURIComponent(self.mContent.SocialDescription),
              '&in_loc=' + self.getLocation(),
            ].join(''); */

    if (App.CORDOVA == false) {
      window.open(uri, '_blank');
    } else {
      App.INSTANCE.openExternalUrl(uri);
    }
  };

  const containerClassName = props.content.Location ? 'add_to_calendar bumpDownCalendar' : 'add_to_calendar';
  return (
    <div className={containerClassName}>
      <button
        className="add_to_calendar_button"
        tabIndex={0}
        aria-label={`Add ${props.content.Name} to calendar`}
        onClick={handleCalendarButtonClick}
        style={{ border: 'none', background: 'none' }}
      >
        <span className="fa-stack calendar_icon_container">
          <i className="fa-thin fa-circle fa-stack-2x calendar_icon_background brand_color_color"></i>
          <i
            className={`fa-stack-1x calendar_icon ${FontAwesomeConfigurations.ICON_CALENDAR_CIRCLE_PLUS} brand_color_color`}
          ></i>
        </span>
        {props.helperText && <div className="calendar_icon_text">{props.helperText}</div>}
      </button>
      {optionsOpen && (
        <div ref={optionsRef} id="event_share_popover">
          {calendarButtons}
        </div>
      )}
    </div>
  );
};

const promptLogin = () => {
  App.INSTANCE.loginOrCreate({
    forceLogin: CrowdHub_App.CONFIG.REQUIRE_GLOBAL_LOGIN,
    aOnComplete: () => {
      if (App.INSTANCE.CurrentUser.IsLoggedIn()) {
        trackDownloadClick(a.mDownload);
      }
    },
    aMessage: CrowdHub_App.LANG.LOGINREASON_DEFAULT,
  });
};

const openOptions = setOptionsOpen => {
  const handleEscape = key => {
    if (key.code === 'Escape') {
      setOptionsOpen(false);
      document.removeEventListener('keyup', handleEscape);
    } else {
      document.addEventListener('keyup', handleEscape, { once: true });
    }
  };
  document.addEventListener('keydown', handleEscape, { once: true });
  Accessibility.focusedElement = document.getElementsByClassName('add_to_calendar_button')[0] as HTMLElement;
  setOptionsOpen(true);
};

function useOptionsPopoverListener(ref, optionsOpen, setOptionsOpen) {
  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        setOptionsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref]);
}

const getLocation = content => {
  const url = CrowdHub_App.CONFIG.UNIVERSAL_FORWARD_URL;

  let hostname = window.location.hostname;
  let route = App.INSTANCE.getContentUrl(content);

  if (App.CORDOVA) {
    hostname = new URL(CrowdHub_App.CONFIG.BASE_URL).hostname;
    route = App.INSTANCE.getContentUrl(content).replace(new RegExp('^' + App.INSTANCE.appRouter.getHash()), '');
  }

  return `${url}${hostname}${route}`;
};

const formatDate = date => {
  return date.format('YYYYMMDDTHHmmss');
};

const formatMSDate = date => {
  return `${date.format('YYYY-MM-DDTHH:mm:ss')}`;
};

const joinEvent = content => {
  let aAction = content.GetActionById(CrowdHub_App.ACTION_JOINED);
  if (aAction && aAction.UserCompleted == false) {
    aAction.Complete(
      aAction.ContentId,
      function () {},
      function (a) {},
      false,
    );
  }

  aAction = content.GetActionById(CrowdHub_App.ACTION_ATTEND);
  if (aAction && aAction.UserCompleted == false) {
    aAction.Complete(
      aAction.ContentId,
      function () {},
      function (a) {},
      false,
    );
  }
};

export default JUNOAddToCalendarButton;
